import vr from "../../../utilFunctions/validationRules";

const {
    lenBetween,
    isIdOrRelation,
    isNonEmptyArrayOfIdOrRelation,
    optionalLenBetween,
} = vr;

export const isEmpty = (v) =>
    v == null ||
    (typeof v === "string" && v.trim() === "") ||
    (Array.isArray(v) && v.length === 0);

export const buildTeamValidation = (t) => ({
    name: {
        required: true,
        requiredMessage: t?.common?.required_field ,
        invalidMessage:
            t?.common?.minmax_chars?.replace?.("{min}", "3")?.replace?.("{max}", "150"),
        pattern: (v) => lenBetween(v, 3, 150),
        error: true,
        skipped: false,
    },
    description: {
        required: false,
        invalidMessage:
            t?.common?.minmax_chars?.replace?.("{min}", "0")?.replace?.("{max}", "256"),
        pattern: (v) => optionalLenBetween ? optionalLenBetween(v, 0, 256) : true,
        error: false,
        skipped: true,
    },
    users: {
        required: true,
        requiredMessage: t?.common?.required_field,
        invalidMessage: t?.common?.invalid,
        pattern: (arr) => isNonEmptyArrayOfIdOrRelation(arr),
        error: true,
        skipped: false,
    },
    superior: {
        required: true,
        requiredMessage: t?.common?.required_field,
        invalidMessage: t?.common?.invalid,
        pattern: (v) => isIdOrRelation(v),
        error: true,
        skipped: false,
    },
    customers: {
        required: true,
        requiredMessage: t?.common?.required_field,
        invalidMessage: t?.common?.invalid,
        pattern: (arr) => isNonEmptyArrayOfIdOrRelation(arr),
        error: true,
        skipped: false,
    },
    locations: {
        required: true,
        requiredMessage: t?.common?.required_field,
        invalidMessage: t?.common?.invalid,
        pattern: () => true,
        error: true,
        skipped: false,
    },
    image: {
        required: false,
        invalidMessage: t?.common?.invalid,
        pattern: () => true,
        error: false,
        skipped: true,
    },
});

export const computeValidation = (value, rule, requiredFallback = "Required") => {
    const empty = isEmpty(value);
    const passes = rule.pattern ? rule.pattern(value) : true;

    const error = rule.required ? (empty || (!empty && !passes)) : (!empty && !passes);
    const message =
        empty && rule.required
            ? rule.requiredMessage ?? requiredFallback
            : !passes
                ? rule.invalidMessage ?? rule.message ?? "Invalid value"
                : "";

    return { error, message, skipped: true };
};

export const makeOnBlurFactory = ({ getData, setValidation }) => {
    return (key, options = {}) => () => {
        const { valueSelector = (d) => d[key], requiredSelector } = options;

        setValidation((prev) => {
            const rule = prev[key];
            if (!rule) return prev;

            const data = getData();
            const val = valueSelector(data);
            const effRule = requiredSelector ? { ...rule, required: !!requiredSelector(data) } : rule;

            return { ...prev, [key]: { ...rule, ...computeValidation(val, effRule) } };
        });
    };
};

export const makeOnValuesChangeFactory = ({
                                              getData,
                                              setData,
                                              setValidation,
                                              vKeyOf = (n) => n,
                                              setInitChange,
                                          }) => {
    return (name, options = {}) => (event, uiData) => {
        const { requiredSelector, valueSelector } = options;
        if (typeof setInitChange === "function") setInitChange(true);

        const dropdownFields = ["users", "superior", "customers", "locations"];
        const booleanFields = [];

        let currentFieldValue = "";

        if (!dropdownFields.includes(name) && !booleanFields.includes(name)) {
            const raw = event?.target?.value ?? "";
            currentFieldValue = valueSelector ? valueSelector(raw, { event, data: uiData }) : raw;
            setData((prev) => ({ ...prev, [name]: currentFieldValue }));
        }

        if (dropdownFields.includes(name)) {
            const raw = uiData?.value;
            currentFieldValue = valueSelector ? valueSelector(raw, { event, data: uiData }) : raw;
            setData((prev) => ({ ...prev, [name]: currentFieldValue }));
        }

        if (booleanFields.includes(name)) {
            const raw = !!uiData?.checked;
            currentFieldValue = valueSelector ? valueSelector(raw, { event, data: uiData }) : raw;
            setData((prev) => ({ ...prev, [name]: currentFieldValue }));
        }

        setValidation((prev) => {
            const key = vKeyOf(name);
            const rule = prev[key];
            if (!rule) return prev;

            const dataSnapshot = getData();
            const effRule = requiredSelector ? { ...rule, required: !!requiredSelector(dataSnapshot) } : rule;
            const res = computeValidation(currentFieldValue, effRule);

            return {
                ...prev,
                [key]: {
                    ...effRule,
                    ...res,
                },
            };
        });
    };
};

export const makeValidateOnSubmitFactory = ({
                                                getData,
                                                validation,
                                                setValidation,
                                                requiredSelectors = {},
                                            }) => {
    return () => {
        const d = getData();
        const next = { ...validation };
        const keys = Object.keys(next);

        keys.forEach((k) => {
            const baseRule = next[k];
            if (!baseRule) return;

            const effRule = requiredSelectors[k]
                ? { ...baseRule, required: !!requiredSelectors[k](d) }
                : baseRule;

            const val = d[k];
            const res = computeValidation(val, effRule);
            next[k] = { ...effRule, ...res };
        });

        setValidation(next);
        return keys.some((k) => next[k]?.error);
    };
};

const calcLocationMismatch = (selectedLocationIds = [], customerIds = [], options) => {
    const customers = options?.customers || [];
    const requiredLocIds = [];
    const picked = customers.filter(c => (customerIds || []).includes(c.value));

    picked.forEach(c => (c.locations || []).forEach(l => requiredLocIds.push(l.id)));

    const missing = requiredLocIds.filter(id => !selectedLocationIds.includes(id));

    const label =
        missing.length > 0
            ? customers
                .filter(c => (customerIds || []).includes(c.value) &&
                    (c.locations || []).some(l => missing.includes(l.id)))
                .map(c => c.text)
                .join(", ")
            : "";

    return { missing, label };
};

const patchLocationsValidation = ({ setValidation, t, selectedLocation, customers, options }) => {
    const { missing, label } = calcLocationMismatch(selectedLocation, customers, options);

    setValidation(prev => ({
        ...prev,
        locations: {
            ...prev.locations,
            required: true,
            skipped: true,
            error: missing.length > 0 || !(Array.isArray(selectedLocation) && selectedLocation.length > 0),
            message: label
                ? `${t.teams.location_valid} ${label} ${t.teams.selected_data}`
                : (t.common?.required_field || "Required"),
        },
    }));

    return { missing, label };
};

export const makeTeamOnValuesChangeFactory = ({
                                                  getData,
                                                  setData,
                                                  setValidation,
                                                  setInitChange,
                                                  getOptions,
                                                  setOptions,
                                                  t,
                                                  setMatchingCustomers,
                                              }) => {
    const base = makeOnValuesChangeFactory({ getData, setData, setValidation, setInitChange });

    return (name) => (event, ui) => {
        const options = getOptions?.() || {};
        const data = getData();

        if (name !== "customers" && name !== "locations") {
            return base(name)(event, ui);
        }

        if (name === "customers") {
            const selected = Array.isArray(ui?.value) ? ui.value : [];

            const initOpts = options?.locations?.initOptions || [];
            let nextLocations = [...(data.locations || [])];
            let nextSelectedLocation = [...(data.selectedLocation || [])];
            let filteredInit = [...initOpts];

            const picked = (options.customers || []).filter(c => selected.includes(c.value));

            picked.forEach(c => {
                (c.locations || []).forEach(loc => {
                    if (!nextLocations.includes(loc.id)) {
                        if (loc.parentId) {
                            const parentChosen =
                                (c.locations || []).some(l => l.id === loc.parentId) ||
                                nextLocations.includes(loc.parentId);
                            if (parentChosen) {
                                nextLocations.push(loc.parentId);
                                nextSelectedLocation.push(loc.parentId);

                                const subs = initOpts.filter(o => o.parentId === loc.parentId);
                                subs.forEach(s => nextSelectedLocation.push(s.value));
                                filteredInit = filteredInit.filter(o => !subs.some(s => s.value === o.value));
                            } else {
                                nextLocations.push(loc.id);
                                nextSelectedLocation.push(loc.id);
                            }
                        } else {
                            nextLocations.push(loc.id);
                            nextSelectedLocation.push(loc.id);
                        }
                    }
                });
            });

            nextLocations = [...new Set(nextLocations)];
            nextSelectedLocation = [...new Set(nextSelectedLocation)];

            setData(prev => ({
                ...prev,
                customers: selected,
                locations: nextLocations,
                selectedLocation: nextSelectedLocation,
            }));
            setOptions(prev => ({ ...prev, locations: { ...prev.locations, options: filteredInit } }));

            base("customers")(event, { value: selected });

            const { label } = patchLocationsValidation({
                setValidation,
                t,
                selectedLocation: nextSelectedLocation,
                customers: selected,
                options,
            });
            setMatchingCustomers(label);
            return;
        }

        if (name === "locations") {
            const picked = Array.isArray(ui?.value) ? ui.value : [];
            const initOpts = options?.locations?.initOptions || [];

            const selectedOpts = initOpts.filter(o => picked.includes(o.value));
            let pruned = [...picked];
            let expanded = [...picked];
            let filteredInit = [...initOpts];

            selectedOpts.forEach(opt => {
                if (!opt.disabled) {
                    const subs = initOpts.filter(s => s.parentId === opt.key);
                    expanded = [...expanded, ...subs.map(s => s.value)];
                    if (subs.length > 0) {
                        pruned = pruned.filter(v => !subs.some(s => s.value === v));
                    }
                    filteredInit = filteredInit.filter(o => o.parentId !== opt.key);
                }
            });

            const nextLocations = [...new Set(pruned)];
            const nextSelectedLocation = [...new Set(expanded)];

            setData(prev => ({
                ...prev,
                locations: nextLocations,
                selectedLocation: nextSelectedLocation,
            }));
            setOptions(prev => ({ ...prev, locations: { ...prev.locations, options: filteredInit } }));

            base("locations")(event, { value: nextLocations });

            const { label } = patchLocationsValidation({
                setValidation,
                t,
                selectedLocation: nextSelectedLocation,
                customers: data.customers,
                options,
            });
            setMatchingCustomers(label);
        }
    };
};

export const makeTeamValidateOnSubmitFactory = ({
                                                    getData,
                                                    validation,
                                                    setValidation,
                                                    getOptions,
                                                    t,
                                                    setMatchingCustomers,
                                                }) => {
    const base = makeValidateOnSubmitFactory({ getData, validation, setValidation });

    return () => {
        const hasErrors = base();
        const d = getData();
        const options = getOptions?.() || {};

        const { label, missing } = calcLocationMismatch(d.selectedLocation || [], d.customers || [], options);
        setMatchingCustomers(label);

        patchLocationsValidation({
            setValidation,
            t,
            selectedLocation: d.selectedLocation || [],
            customers: d.customers || [],
            options,
        });

        return hasErrors || (missing && missing.length > 0);
    };
};
